import React from "react";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { push } from "store/actions/router";
import { Col, Row } from "react-bootstrap";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { userConstants } from "actions/types";
import { updateUserProfile } from "actions/userActions";
import type { RootState } from "types/state";
import type { IraAccount } from "types/graphqlSchema";

import UserProfileWorkflow from "pages/dashboards/individualDashboard/profile/UserProfileWorkflow";
import IconHeader from "components/IconHeader";
import Button from "components/Button";
import IconSubheader from "components/IconSubheader";

import "../../profile/ProfileInfoCard.scss";

interface ProfileInfo {
  firstName?: string;
  lastName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  phoneNumber?: string;
}

interface OwnProps {
  fetchingProfile?: boolean;
  goToNamedStep?: (name: string) => void;
  nextStepName: string;
  isAccountClosure?: boolean;
  // react-step-wizard injects stepName at runtime; the workflow declares it
  // on the JSX element. Optional so the parent may set it.
  stepName?: string;
  // Inert pass-throughs from the workflows — declared so the JSX type-checks;
  // the component reads none of them (it renders userInfo from redux).
  dob?: string;
  accounts?: IraAccount[];
  isRothDistribution?: boolean;
  selectedAccountType?: string;
}

const profileUpdateSelector = createLoadingSelector(
  userConstants.USER_PROFILE_UPDATE
);
const profileUpdateErrorSelector = createErrorSelector(
  userConstants.USER_PROFILE_UPDATE
);
const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
  const { id, profile: userInfo } = state.user;

  return {
    id,
    // profile carries an index signature (unknown fields); cast to the
    // rendered shape so JSX field access is typed (mirrors UserProfileWorkflow)
    userInfo: userInfo as ProfileInfo,
    ownProps,
    isUpdatingProfile: profileUpdateSelector(state),
    error: profileUpdateErrorSelector(state),
  };
};

const mapDispatchToProps = { push, updateUserProfile };

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  isEditMode: boolean;
}

class AddressConfirmation extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      isEditMode: false,
    };
  }

  handleCancel = () => {
    if (this.props.isAccountClosure) {
      this.props.push("/dashboard/profile/close-account");
    } else {
      this.props.push("/dashboard/support");
    }
  };

  render() {
    var articleStyle = {
      paddingBottom: 0,
    };
    return (
      <div id="account-selection">
        {!this.state.isEditMode && (
          <div className="mega-container">
            <section className="page-title-wrap" style={{ paddingTop: 20 }}>
              <article className="text-cell">
                <IconHeader
                  variant="pageHeader"
                  headerText="Please confirm that this information is current before
                  proceeding."
                />
                <IconSubheader
                  variant="subheading"
                  subheader=" We keep this on file for regulatory purposes and if
                  you ever request a withdrawal, so please confirm it, as we
                  will be mailing you a check once your withdrawal is complete."
                />
              </article>
            </section>
            <section className="form-sec-2col" style={{ paddingTop: 35 }}>
              <article className="col-form" style={articleStyle}>
                <div id="form-individual-info" style={{ paddingTop: 20 }}>
                  <Row style={{ paddingBottom: 20 }}>
                    <Col md={4}>
                      <IconHeader
                        variant="labelHeader"
                        headerText="Legal Name"
                      />
                    </Col>
                    <Col md={6}>
                      <p className="address-confirmation">
                        {this.props.userInfo.firstName}{" "}
                        {this.props.userInfo.lastName}
                      </p>
                    </Col>
                  </Row>

                  <Row style={{ paddingBottom: 20 }}>
                    <Col md={4}>
                      <IconHeader
                        variant="labelHeader"
                        headerText="Residential Address"
                      />
                    </Col>
                    <Col md={6}>
                      <p className="address-confirmation">
                        {this.props.userInfo.address1}{" "}
                        {this.props.userInfo.address2}
                      </p>
                      <p
                        className="address-confirmation"
                        style={{ display: "flex" }}
                      >
                        {this.props.userInfo.city} {this.props.userInfo.state}{" "}
                        {this.props.userInfo.postalCode}
                      </p>
                    </Col>
                  </Row>

                  <Row style={{ paddingBottom: 20 }}>
                    <Col md={4}>
                      <IconHeader
                        variant="labelHeader"
                        headerText="Phone Number"
                      />
                    </Col>
                    <Col md={6}>
                      <p className="address-confirmation">
                        {this.props.userInfo.phoneNumber}
                      </p>
                    </Col>
                  </Row>
                </div>
              </article>
            </section>
            <div
              className="submit-row btn-row"
              style={{ justifyContent: "space-between" }}
            >
              <span>
                <Button
                  type="button"
                  color="cancel"
                  name="cancel"
                  btnLabel="Cancel"
                  onClick={this.handleCancel}
                />
              </span>
              <span style={{ display: "flex", gap: 10 }}>
                <Button
                  className="edit"
                  type="button"
                  color="action"
                  name="action"
                  btnLabel="Update Info"
                  onClick={() => {
                    this.setState({ isEditMode: true });
                  }}
                />
                <Button
                  name="action"
                  onClick={() =>
                    // PRESERVED BUG (conversion-only): goToNamedStep is injected by StepWizard but optional in the contract; `!` preserves throw-on-undefined
                    this.props.goToNamedStep!(this.props.nextStepName)
                  }
                />
              </span>
            </div>
          </div>
        )}
        {!this.props.fetchingProfile && this.state.isEditMode && (
          <UserProfileWorkflow
            onSuccess={() => {
              this.setState({ isEditMode: false });
              // PRESERVED BUG (conversion-only): goToNamedStep is injected by StepWizard but optional in the contract; `!` preserves throw-on-undefined
              this.props.goToNamedStep!(this.props.nextStepName);
            }}
            onCancel={() => this.setState({ isEditMode: false })}
          />
        )}
      </div>
    );
  }
}

export default connector(withApollo<Props>(AddressConfirmation));
